import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, MailCheck, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 transition-all focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30";

export default function LeadForm() {
  const [form, setForm] = useState({ first_name: "", email: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        first_name: form.first_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        origin: window.location.origin,
      });
      setDone(true);
      toast.success("Your free eBook is on its way!");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="lead-form-success-message"
        className="flex flex-col items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/70 px-6 py-8 text-center"
      >
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-blue-700 text-white">
          <MailCheck className="h-6 w-6" />
        </span>
        <p className="font-display text-lg font-bold text-slate-900">You're in, {form.first_name}!</p>
        <p className="text-sm text-slate-600">
          Your free copy of <span className="font-semibold">The Change ¹⁷</span> is headed to{" "}
          <span className="font-semibold">{form.email}</span>. Keep an eye on your inbox.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={submit} className="space-y-3">
      <input
        data-testid="lead-form-first-name-input"
        type="text"
        required
        placeholder="First Name"
        value={form.first_name}
        onChange={update("first_name")}
        className={inputClass}
        aria-label="First name"
      />
      <input
        data-testid="lead-form-email-input"
        type="email"
        required
        placeholder="Email Address"
        value={form.email}
        onChange={update("email")}
        className={inputClass}
        aria-label="Email address"
      />
      <input
        data-testid="lead-form-phone-input"
        type="tel"
        placeholder="Phone Number (Optional)"
        value={form.phone}
        onChange={update("phone")}
        className={inputClass}
        aria-label="Phone number (optional)"
      />
      <button
        data-testid="lead-form-submit-button"
        type="submit"
        disabled={loading}
        className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-700 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-blue-700/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-blue-800 hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-70"
      >
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Me the Free eBook"}
      </button>
      <p data-testid="lead-form-microcopy" className="flex items-start gap-1.5 pt-1 text-xs leading-relaxed text-slate-500">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0 text-blue-600" />
        No spam. Just real strategies for better sleep, sharper focus, and a life that runs on
        purpose. Unsubscribe anytime.
      </p>
    </form>
  );
}
